import Image from 'next/image';
import { FaTimesCircle, FaCheckCircle } from 'react-icons/fa';

import type {
  ComponentsComparisonTable,
  ComponentsComparisonTableRow,
  ComponentsComparisonTableStyle,
} from 'queries/graphql';

import { buildImgProps } from 'utils/media';
import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';

import styles from './styles.module.scss';

const iconsMap = {
  checkMark: {
    icon: <FaCheckCircle size="28px" />,
    iconStyle: styles.check_mark_color,
  },
  xMark: {
    icon: <FaTimesCircle size="28px" />,
    iconStyle: styles.x_mark_color,
  },
};

export default function ComparisonTable({
  rows,
  tableStyle,
}: ComponentsComparisonTable & { tableStyle: ComponentsComparisonTableStyle }) {
  const {
    headingColor,
    textColor,
    backgroundColor,
    highlightColor,
    checkMarkColor,
    xMarkColor,
    headingFont,
    textFont,
  } = tableStyle || {};

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);
  return (
    <div className={`root ${styles.comparison_chart_wrapper}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--heading-color', headingColor, 'var(--heading-color-dark)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
          ${buildCssVar('--highlight-color', highlightColor, 'var(--secondary-color)')}
          ${buildCssVar('--check-mark-color', checkMarkColor, 'var(--color-positive)')}
          ${buildCssVar('--x-mark-color', xMarkColor, 'var(--color-negative)')}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <div className={styles.comparison_chart}>
        {rows.map((row, i) => (
          <div key={row.id} className={styles.comparison_chart_row}>
            {renderCell(i, row, 0)}
          </div>
        ))}
      </div>
    </div>
  );
}

function renderCell(rowIndex: number, item: ComponentsComparisonTableRow, colIndex: number) {
  const key = `${rowIndex}.${colIndex}`;
  const isHighlighted = colIndex === 1;

  switch (item?.type) {
    case 'icon': {
      const { icon, iconStyle } = iconsMap[item.icon];
      const classNames = [styles.icon_wrapper, iconStyle];

      if (isHighlighted) classNames.push(styles.highlight);

      return (
        <div key={key} className={classNames.join(' ')}>
          {icon}
        </div>
      );
    }

    case 'text': {
      const { text, image } = item;
      const isHeading = rowIndex === 0 || colIndex === 0;
      const TagName = isHeading ? 'h4' : 'p';
      const classNames = [styles.row_content];

      if (rowIndex === 0) classNames.push(styles.first_row);
      if (isHighlighted) classNames.push(styles.highlight);

      return (
        <div key={key} className={classNames.join(' ')}>
          {image && <Image {...buildImgProps(image)} alt={image.alt} />}
          <TagName>{text}</TagName>
        </div>
      );
    }
  }
}
